import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import {
  Loader2, Plus, X, ArrowLeft, Archive, ArchiveRestore, Trash2, Pencil, Save, ClipboardList,
  FolderKanban, CheckCircle2, RotateCcw, Search,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { tInput, tLabel, tBtn, tBtnGhost } from "./trackUi";

const CATEGORIES = ["speed", "agility", "power", "strength", "endurance", "custom"];
const MEASURES = [
  { v: "time", label: "Time (lower is better)" },
  { v: "distance", label: "Distance (higher is better)" },
  { v: "count", label: "Count / Reps (higher is better)" },
  { v: "weight", label: "Weight (higher is better)" },
];
const parseAttempts = (str) => str.split(/[,\s]+/).map((s) => s.trim()).filter(Boolean).map((raw) => ({ raw }));

/* ---------------- Test Catalog ---------------- */
function TestForm({ orgId, initial, onCancel, onSaved }) {
  const editing = !!initial?.id;
  const [form, setForm] = useState(() => ({
    name: initial?.name || "", category: initial?.category || "custom", unit: initial?.unit || "",
    measure: initial?.measure || "time", lower_better: initial?.lower_better ?? true, description: initial?.description || "",
  }));
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const onMeasure = (m) => setForm((f) => ({ ...f, measure: m, lower_better: m === "time" }));
  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    const payload = { ...form };
    try {
      if (editing) await api.patch(`/track/tests/${initial.id}`, payload);
      else await api.post("/track/tests", { ...payload, organization_id: orgId });
      toast.success(editing ? "Test updated." : "Test created."); onSaved();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setSaving(false); }
  };
  return (
    <form onSubmit={submit} className="bg-[#121212] border border-white/10 p-6 mb-6 space-y-4" data-testid="test-form">
      <div className="grid sm:grid-cols-2 gap-4">
        <div><label className={tLabel}>Test Name *</label><input data-testid="test-name" required value={form.name} onChange={(e) => set("name", e.target.value)} className={tInput} placeholder="40 Yard Dash" /></div>
        <div><label className={tLabel}>Category</label><select value={form.category} onChange={(e) => set("category", e.target.value)} className={tInput}>{CATEGORIES.map((c) => <option key={c} value={c}>{c[0].toUpperCase() + c.slice(1)}</option>)}</select></div>
        <div><label className={tLabel}>Measure</label><select data-testid="test-measure" value={form.measure} onChange={(e) => onMeasure(e.target.value)} className={tInput}>{MEASURES.map((m) => <option key={m.v} value={m.v}>{m.label}</option>)}</select></div>
        <div><label className={tLabel}>Unit</label><input data-testid="test-unit" value={form.unit} onChange={(e) => set("unit", e.target.value)} className={tInput} placeholder="s / in / reps / lbs" /></div>
      </div>
      <div><label className={tLabel}>Description</label><input value={form.description} onChange={(e) => set("description", e.target.value)} className={tInput} /></div>
      <div className="flex gap-2">
        <button data-testid="test-submit" disabled={saving} className={tBtn}>{saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />} {editing ? "Save Changes" : "Create Test"}</button>
        <button type="button" onClick={onCancel} className={tBtnGhost}><X size={14} /> Cancel</button>
      </div>
    </form>
  );
}

function TestCatalog({ orgId, canWrite }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("active");
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (orgId) p.set("organization_id", orgId);
      p.set("status", statusFilter);
      const { data } = await api.get(`/track/tests?${p.toString()}`);
      setList(data);
    } catch { toast.error("Failed to load tests."); } finally { setLoading(false); }
  }, [orgId, statusFilter]);
  useEffect(() => { load(); }, [load]);

  const archive = async (t) => {
    const next = t.status === "archived" ? "active" : "archived";
    try { await api.patch(`/track/tests/${t.id}`, { status: next }); load(); } catch { toast.error("Failed."); }
  };
  const del = async (t) => {
    if (!window.confirm(`Delete "${t.name}"? Existing results keep their data.`)) return;
    try { await api.delete(`/track/tests/${t.id}`); toast.success("Deleted."); load(); } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const byCat = list.reduce((acc, t) => { (acc[t.category] = acc[t.category] || []).push(t); return acc; }, {});

  return (
    <div data-testid="test-catalog">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="flex gap-1.5" data-testid="catalog-status-filter">
          {["active", "archived", "all"].map((s) => (
            <button key={s} onClick={() => setStatusFilter(s)} className={`px-3 py-2 text-xs uppercase tracking-widest font-bold border transition-colors ${statusFilter === s ? "bg-white text-black border-white" : "border-white/15 text-white/50 hover:border-white/40"}`}>{s}</button>
          ))}
        </div>
        {canWrite && <button data-testid="add-test-btn" onClick={() => { setShowForm((v) => !v); setEditing(null); }} className={tBtn}>{showForm ? <X size={14} /> : <Plus size={14} />} {showForm ? "Cancel" : "New Test"}</button>}
      </div>
      {showForm && canWrite && <TestForm orgId={orgId} onCancel={() => setShowForm(false)} onSaved={() => { setShowForm(false); load(); }} />}
      {editing && canWrite && <TestForm orgId={orgId} initial={editing} onCancel={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}

      {loading ? <div className="flex justify-center py-16 text-white/40"><Loader2 className="animate-spin" /></div>
        : list.length === 0 ? <div className="border border-white/10 bg-[#121212] p-6 text-white/40 text-sm" data-testid="catalog-empty">No tests found.</div>
        : (
          <div className="space-y-6">
            {Object.entries(byCat).map(([cat, tests]) => (
              <div key={cat}>
                <p className="font-mono-accent text-[10px] tracking-[0.25em] uppercase text-white/40 mb-2">{cat}</p>
                <div className="border border-white/10 bg-[#121212] divide-y divide-white/5">
                  {tests.map((t) => (
                    <div key={t.id} data-testid={`test-row-${t.id}`} className="flex items-center justify-between px-4 py-3 flex-wrap gap-2">
                      <div>
                        <span className="font-bold">{t.name}</span>
                        {t.is_standard && <span className="ml-2 font-mono-accent text-[9px] tracking-[0.2em] uppercase text-white/30">Standard</span>}
                        {t.status === "archived" && <span className="ml-2 font-mono-accent text-[9px] tracking-[0.2em] uppercase border border-white/15 px-1.5 py-0.5 text-white/40">Archived</span>}
                        <p className="text-white/40 text-xs mt-0.5">{t.unit || "—"} · {t.lower_better ? "lower is better" : "higher is better"}</p>
                      </div>
                      {canWrite && (
                        <div className="flex items-center gap-2">
                          <button data-testid={`edit-test-${t.id}`} onClick={() => { setEditing(t); setShowForm(false); }} className="text-white/30 hover:text-white transition-colors"><Pencil size={15} /></button>
                          <button data-testid={`archive-test-${t.id}`} onClick={() => archive(t)} className="text-white/30 hover:text-white transition-colors">{t.status === "archived" ? <ArchiveRestore size={15} /> : <Archive size={15} />}</button>
                          {isAdmin && <button data-testid={`del-test-${t.id}`} onClick={() => del(t)} className="text-white/25 hover:text-red-400 transition-colors"><Trash2 size={15} /></button>}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
    </div>
  );
}

/* ---------------- Group Testing Sessions ---------------- */
function SessionForm({ orgId, onCancel, onSaved }) {
  const [groups, setGroups] = useState([]);
  const [tests, setTests] = useState([]);
  const [form, setForm] = useState({ name: "", date: new Date().toISOString().slice(0, 10), season: "", group_id: "", test_ids: [] });
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    const p = orgId ? `?organization_id=${orgId}` : "";
    Promise.all([api.get(`/track/groups${p}${p ? "&" : "?"}status=active`), api.get(`/track/tests${p}${p ? "&" : "?"}status=active`)])
      .then(([g, t]) => { setGroups(g.data); setTests(t.data); }).catch(() => {});
  }, [orgId]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleTest = (id) => setForm((f) => ({ ...f, test_ids: f.test_ids.includes(id) ? f.test_ids.filter((x) => x !== id) : [...f.test_ids, id] }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.group_id) { toast.error("Select a group."); return; }
    if (!form.test_ids.length) { toast.error("Select at least one test."); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/track/test-sessions", { ...form, organization_id: orgId });
      toast.success("Session created."); onSaved(data.id);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setSaving(false); }
  };
  return (
    <form onSubmit={submit} className="bg-[#121212] border border-white/10 p-6 mb-6 space-y-4" data-testid="session-form">
      <div className="grid sm:grid-cols-3 gap-4">
        <div><label className={tLabel}>Session Name *</label><input data-testid="session-name" required value={form.name} onChange={(e) => set("name", e.target.value)} className={tInput} placeholder="Spring Combine" /></div>
        <div><label className={tLabel}>Date</label><input type="date" value={form.date} onChange={(e) => set("date", e.target.value)} className={tInput} /></div>
        <div><label className={tLabel}>Season</label><input value={form.season} onChange={(e) => set("season", e.target.value)} className={tInput} placeholder="2026" /></div>
      </div>
      <div><label className={tLabel}>Group *</label><select data-testid="session-group" required value={form.group_id} onChange={(e) => set("group_id", e.target.value)} className={tInput}><option value="">— Select a group —</option>{groups.map((g) => <option key={g.id} value={g.id}>{g.name} ({g.member_count})</option>)}</select></div>
      <div>
        <label className={tLabel}>Tests *</label>
        <div className="flex flex-wrap gap-1.5" data-testid="session-test-picker">
          {tests.map((t) => <button type="button" key={t.id} onClick={() => toggleTest(t.id)} className={`px-2.5 py-1 text-xs border transition-colors ${form.test_ids.includes(t.id) ? "bg-white text-black border-white" : "border-white/15 text-white/50 hover:border-white/40"}`}>{t.name}</button>)}
        </div>
      </div>
      <div className="flex gap-2">
        <button data-testid="session-submit" disabled={saving} className={tBtn}>{saving ? <Loader2 size={14} className="animate-spin" /> : <Plus size={14} />} Create Session</button>
        <button type="button" onClick={onCancel} className={tBtnGhost}><X size={14} /> Cancel</button>
      </div>
    </form>
  );
}

function SessionCell({ sessionId, athleteId, test, existing, canWrite, onSaved }) {
  const [attempts, setAttempts] = useState(existing ? existing.attempts.map((a) => a.raw).join(", ") : "");
  const [official, setOfficial] = useState(existing && existing.official_source === "manual" ? String(existing.official_value) : "");
  const [saving, setSaving] = useState(false);
  const [display, setDisplay] = useState(existing ? existing.official_display : null);
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/track/test-sessions/${sessionId}/entry`, {
        athlete_id: athleteId, test_id: test.id, attempts: parseAttempts(attempts),
        official_value: official === "" ? null : Number(official),
      });
      setDisplay(data.cleared ? null : data.official_display);
      onSaved?.();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setSaving(false); }
  };
  return (
    <div className="flex flex-col gap-1 min-w-[150px]">
      <input data-testid={`cell-attempts-${athleteId}-${test.id}`} disabled={!canWrite} value={attempts} onChange={(e) => setAttempts(e.target.value)} onBlur={save}
        className="w-full bg-[#0A0A0A] border border-white/15 focus:border-white outline-none px-2 py-1.5 text-sm" placeholder="attempts" />
      <div className="flex items-center gap-1">
        <input data-testid={`cell-official-${athleteId}-${test.id}`} disabled={!canWrite} value={official} onChange={(e) => setOfficial(e.target.value)} onBlur={save}
          className="w-20 bg-[#0A0A0A] border border-white/10 focus:border-white outline-none px-2 py-1 text-xs" placeholder="official" title="Optional official override" />
        <span className="text-[11px] text-white/50">{saving ? "…" : display ? `= ${display}` : ""}</span>
      </div>
    </div>
  );
}

function SessionDetail({ sessionId, canWrite, onBack, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/track/test-sessions/${sessionId}`); setData(data); }
    catch { toast.error("Failed to load session."); } finally { setLoading(false); }
  }, [sessionId]);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (aid, status) => {
    try {
      await api.patch(`/track/test-sessions/${sessionId}`, { athlete_statuses: { ...(data.session.athlete_statuses || {}), [aid]: status } });
      setData((d) => ({ ...d, session: { ...d.session, athlete_statuses: { ...d.session.athlete_statuses, [aid]: status } } }));
    } catch { toast.error("Failed to update status."); }
  };
  const toggleComplete = async () => {
    const next = data.session.status === "completed" ? "draft" : "completed";
    try { await api.patch(`/track/test-sessions/${sessionId}`, { status: next }); toast.success(next === "completed" ? "Session completed." : "Session reopened."); load(); onChanged?.(); }
    catch { toast.error("Failed."); }
  };
  const del = async () => {
    if (!window.confirm("Delete this session? Entered results are kept as individual athlete results.")) return;
    try { await api.delete(`/track/test-sessions/${sessionId}`); toast.success("Session deleted."); onChanged?.(); onBack(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  if (loading || !data) return <div className="flex justify-center py-24 text-white/40"><Loader2 className="animate-spin" /></div>;
  const s = data.session;
  const completed = s.status === "completed";

  return (
    <div data-testid="session-detail">
      <button data-testid="session-back" onClick={onBack} className="inline-flex items-center gap-2 text-white/50 hover:text-white text-sm mb-5 transition-colors"><ArrowLeft size={15} /> Back to sessions</button>
      <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
        <div>
          <div className="flex items-center gap-3"><h1 className="font-display font-black uppercase tracking-tighter text-4xl">{s.name}</h1>{completed && <span className="font-mono-accent text-[10px] tracking-[0.2em] uppercase border border-emerald-500/40 text-emerald-400 px-2 py-1">Completed</span>}</div>
          <p className="text-white/40 text-sm mt-1">{s.date}{s.season ? ` · ${s.season}` : ""} · {data.roster.length} athletes · {data.tests.length} test(s)</p>
        </div>
        {canWrite && (
          <div className="flex gap-2 flex-wrap">
            <button data-testid="session-complete" onClick={toggleComplete} className={tBtnGhost}>{completed ? <><RotateCcw size={14} /> Reopen</> : <><CheckCircle2 size={14} /> Mark Complete</>}</button>
            <button data-testid="session-delete" onClick={del} className="inline-flex items-center gap-2 border border-red-500/40 text-red-400 font-bold uppercase tracking-widest text-xs px-4 py-2.5 hover:bg-red-500 hover:text-white transition-colors"><Trash2 size={14} /> Delete</button>
          </div>
        )}
      </div>

      {data.roster.length === 0 ? <div className="border border-white/10 bg-[#121212] p-6 text-white/40 text-sm">This session's group has no athletes.</div>
        : (
          <div className="border border-white/10 bg-[#121212] overflow-x-auto" data-testid="session-grid">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-white/10 text-white/40">
                  <th className="text-left font-mono-accent text-[10px] tracking-[0.2em] uppercase px-4 py-3 sticky left-0 bg-[#121212]">Athlete</th>
                  <th className="text-left font-mono-accent text-[10px] tracking-[0.2em] uppercase px-4 py-3">Status</th>
                  {data.tests.map((t) => <th key={t.id} className="text-left font-mono-accent text-[10px] tracking-[0.2em] uppercase px-4 py-3">{t.name}</th>)}
                </tr>
              </thead>
              <tbody>
                {data.roster.map((a) => (
                  <tr key={a.id} data-testid={`session-row-${a.id}`} className="border-b border-white/5 align-top">
                    <td className="px-4 py-3 font-bold sticky left-0 bg-[#121212]">{a.name}</td>
                    <td className="px-4 py-3">
                      <select data-testid={`status-${a.id}`} disabled={!canWrite} value={s.athlete_statuses?.[a.id] || "present"} onChange={(e) => setStatus(a.id, e.target.value)} className="bg-[#0A0A0A] border border-white/15 outline-none px-2 py-1 text-xs">
                        <option value="present">Present</option><option value="absent">Absent</option><option value="incomplete">Incomplete</option>
                      </select>
                    </td>
                    {data.tests.map((t) => (
                      <td key={t.id} className="px-4 py-3">
                        <SessionCell sessionId={sessionId} athleteId={a.id} test={t} existing={data.results[`${a.id}:${t.id}`]} canWrite={canWrite} onSaved={() => onChanged?.()} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      <p className="text-white/30 text-xs mt-3">Enter attempts (comma separated). Official defaults to the best attempt — type a value in "official" to override. Entries save automatically; drafts resume anytime.</p>
    </div>
  );
}

function SessionsList({ orgId, canWrite, onOpen }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [q, setQ] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get(`/track/test-sessions${orgId ? `?organization_id=${orgId}` : ""}`); setList(data); }
    catch { toast.error("Failed to load sessions."); } finally { setLoading(false); }
  }, [orgId]);
  useEffect(() => { load(); }, [load]);

  const filtered = list.filter((s) => s.name.toLowerCase().includes(q.toLowerCase()));
  return (
    <div data-testid="sessions-list">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="relative flex-1 min-w-[200px]"><Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" /><input data-testid="sessions-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search sessions…" className={`${tInput} pl-9`} /></div>
        {canWrite && <button data-testid="add-session-btn" onClick={() => setShowForm((v) => !v)} className={tBtn}>{showForm ? <X size={14} /> : <Plus size={14} />} {showForm ? "Cancel" : "New Session"}</button>}
      </div>
      {showForm && canWrite && <SessionForm orgId={orgId} onCancel={() => setShowForm(false)} onSaved={(id) => { setShowForm(false); load(); onOpen(id); }} />}
      {loading ? <div className="flex justify-center py-16 text-white/40"><Loader2 className="animate-spin" /></div>
        : filtered.length === 0 ? <div className="border border-white/10 bg-[#121212] p-6 text-white/40 text-sm" data-testid="sessions-empty">No testing sessions yet. {canWrite ? "Create one to test a whole group at once." : ""}</div>
        : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {filtered.map((s) => (
              <button key={s.id} data-testid={`session-card-${s.id}`} onClick={() => onOpen(s.id)} className="text-left bg-[#121212] border border-white/10 hover:border-white/30 transition-colors p-5">
                <div className="flex items-start justify-between gap-2">
                  <p className="font-display font-black uppercase tracking-tight text-xl leading-tight">{s.name}</p>
                  {s.status === "completed" && <CheckCircle2 size={16} className="text-emerald-400 shrink-0" />}
                </div>
                <p className="text-white/40 text-xs mt-1">{s.date}{s.season ? ` · ${s.season}` : ""}</p>
                <div className="flex items-center gap-2 mt-4 text-white/60"><ClipboardList size={15} /><span className="text-sm font-bold">{s.result_count} result{s.result_count === 1 ? "" : "s"}</span></div>
              </button>
            ))}
          </div>
        )}
    </div>
  );
}

/* ---------------- Wrapper ---------------- */
export default function TrackTesting({ orgId, canWrite }) {
  const [tab, setTab] = useState("sessions");
  const [session, setSession] = useState(null);

  if (session)
    return <SessionDetail sessionId={session} canWrite={canWrite} onBack={() => setSession(null)} onChanged={() => {}} />;

  return (
    <div data-testid="track-testing">
      <h1 className="font-display font-black uppercase tracking-tighter text-4xl md:text-5xl mb-6">Testing</h1>
      <div className="flex gap-1 border-b border-white/10 mb-6" data-testid="testing-tabs">
        <button data-testid="testing-tab-sessions" onClick={() => setTab("sessions")} className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm border-b-2 -mb-px transition-colors ${tab === "sessions" ? "border-white text-white font-bold" : "border-transparent text-white/45 hover:text-white/80"}`}><FolderKanban size={15} /> Group Sessions</button>
        <button data-testid="testing-tab-catalog" onClick={() => setTab("catalog")} className={`inline-flex items-center gap-2 px-4 py-2.5 text-sm border-b-2 -mb-px transition-colors ${tab === "catalog" ? "border-white text-white font-bold" : "border-transparent text-white/45 hover:text-white/80"}`}><ClipboardList size={15} /> Test Catalog</button>
      </div>
      {tab === "sessions" ? <SessionsList orgId={orgId} canWrite={canWrite} onOpen={setSession} /> : <TestCatalog orgId={orgId} canWrite={canWrite} />}
    </div>
  );
}
